import React, { useState, useMemo } from "react";
import "./App.css";

import { AppContext } from "./context/AppContext";
import { ThemeProvider } from "styled-components";

import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, Link } from "react-router-dom";

import { Exams, ExamPage, Instruction, Login, LoginWithCode, Results, Layout, Review, ChangePassword, Home, ExamPagePerQuestion, History, Register } from "./pages";
import { decryptData, getFromStorage } from "./assets";
import { MathJaxContext } from "better-react-mathjax";
import useWhitelabel from './hooks/useWhitelabel';


function App() {
	const { isWhitelabel } = useWhitelabel();
	const theme = {
		primary: "#C51C2F",
		secondary: "#FFE0E4",
		white: "white",
		grey: "#4A4A4A",
		text_grey: "#8A8383",
		black: "#000000",
		error: "#FF0000",
		background: "#F2F4F8",
	};

	const [token, setToken] = useState(getFromStorage("studentToken") || null);
	const [user, setUser] = useState(decryptData(getFromStorage("studentUser")) || null);
	const [exam, setExam] = useState(decryptData(getFromStorage("exam")) || null);
	const [school, setSchool] = useState(decryptData(getFromStorage("school")) || null);

	const providerValue = useMemo(
		() => ({
			user,
			setUser,
			exam,
			setExam,
			token,
			setToken,
			school,
			setSchool,
		}),
		[user, setUser, exam, setExam, token, setToken, school, setSchool]
	);

	return (
		<div className="App">
			<Router>
				<ThemeProvider theme={theme}>
					<MathJaxContext>
						<AppContext.Provider value={providerValue}>
							<Routes>
								<Route path="/" element={<Home />} />
								{/* <Route path="/" element={isWhitelabel ? <LoginWithCode /> : <Home />} /> */}
								<Route path="/:school_code/register" element={<Register />} />
								<Route element={<Wrapper token={token} user={user} />}>
									<Route index path="/home" element={<Exams />} />
									<Route path="/profile" element={<ChangePassword />} />
									{!!parseInt(school?.is_tertiary_institution) && <Route path="/history/:exam_id" element={<History />} />}
									<Route path="/exam/:id" element={<Layout />}>
										<Route index element={<Instruction />} />
										<Route path="start" element={exam?.duration_type === "exam" ? <ExamPage /> : <ExamPagePerQuestion />} />
										<Route path="review" element={<Review />} />
										<Route path="end" element={<Results />} />
									</Route>
								</Route>
								<Route path="/login">
									<Route index element={isWhitelabel ? <LoginWithCode /> : <Login />} />
									<Route path=":school_code" element={isWhitelabel ? <Navigate to="/login" replace /> : <LoginWithCode />} />
								</Route>
								<Route path="*" element={<NotFound />} />
							</Routes>
						</AppContext.Provider>
					</MathJaxContext>
				</ThemeProvider>
			</Router>
		</div>
	);
}

function NotFound() {
	return (
		<div
			className="d-flex flex-column align-items-center justify-content-center text-center px-3"
			style={{ minHeight: "100vh" }}
		>
			<svg
				width="220"
				height="160"
				viewBox="0 0 220 160"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				style={{ marginBottom: "1rem" }}
			>
				{/* soft circle backdrop */}
				<circle cx="110" cy="80" r="70" fill="var(--secondary-color)" />

				{/* broken link icon */}
				<g transform="translate(55,55)">
					<rect
						x="0"
						y="20"
						width="45"
						height="22"
						rx="11"
						transform="rotate(-30 22.5 31)"
						fill="none"
						stroke="var(--primary-color)"
						strokeWidth="6"
					/>
					<rect
						x="65"
						y="20"
						width="45"
						height="22"
						rx="11"
						transform="rotate(-30 87.5 31)"
						fill="none"
						stroke="var(--primary-color)"
						strokeWidth="6"
					/>
					<line
						x1="48"
						y1="40"
						x2="62"
						y2="22"
						stroke="var(--primary-color)"
						strokeWidth="6"
						strokeLinecap="round"
						strokeDasharray="4 6"
					/>
				</g>

				{/* scattered dots for texture */}
				<circle cx="40" cy="40" r="4" fill="var(--primary-color)" opacity="0.5" />
				<circle cx="185" cy="50" r="5" fill="var(--primary-color)" opacity="0.4" />
				<circle cx="170" cy="120" r="3.5" fill="var(--primary-color)" opacity="0.5" />
				<circle cx="30" cy="115" r="3" fill="var(--primary-color)" opacity="0.4" />
			</svg>

			<div
				style={{
					fontSize: "9rem",
					fontWeight: 800,
					color: "var(--primary-color)",
					lineHeight: 1,
					letterSpacing: "-0.05em",
				}}
			>
				404
			</div>

			<h2 style={{ fontSize: "2.2rem", fontWeight: 600, color: "#1A1A1A", marginTop: "0.5rem" }}>
				Page not found
			</h2>

			<p style={{ fontSize: "1.6rem", color: "#6B6B6B", maxWidth: "480px", marginTop: "0.5rem" }}>
				The page you're looking for doesn't exist or may have been moved.
			</p>

			<div className="d-flex gap-3 mt-4">
				<button
					onClick={() => window.history.back()}
					style={{
						padding: "10px 25px",
						fontSize: "1.4rem",
						fontWeight: 600,
						color: "#1A1A1A",
						backgroundColor: "transparent",
						border: "2px solid #D1D1D1",
						borderRadius: "var(--border-radius)",
						cursor: "pointer",
					}}
				>
					Go back
				</button>

				<Link to="/login" className="pri-action-btn">
					Go to Login
				</Link>
			</div>
		</div>
	);
}


const Wrapper = ({ token, user }) => {
	if (!token || !user) {
		return <Navigate to="/login" replace />;
	}

	return <Outlet />;
};

export default App;
